import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from '../config/apiConfig';
import { apiClient } from '../config/apiConfig';
import { 
  SUPPORTED_NETWORKS, 
  DEFAULT_NETWORK, 
  DEFAULT_TOKEN,
  SUPPORTED_TOKENS
} from '../config/tokens';
import { TOKENS_BY_NETWORK, getDefaultToken, NetworkConfigService } from '../config/networks';
import ERC20_ABI from '../config/erc20.abi.json';

// Initialize NetworkConfigService singleton
const networkService = NetworkConfigService.getInstance();

const { endpoints } = API_CONFIG;

export const Web3Context = createContext({
  account: null,
  signer: null,
  balance: '0',
  isConnected: false,
  connectWallet: async () => {},
  sendTokenPayment: async () => {},
  updateBalance: async () => {},
  disconnectWallet: () => {},
  selectedNetwork: DEFAULT_NETWORK,
  setSelectedNetwork: () => {},
  selectedToken: DEFAULT_TOKEN,
  setSelectedToken: () => {},
  switchNetwork: async () => {},
  networkError: null,
  isOwnerWallet: false,
  switchToOwnerWallet: async () => {},
  getOwnerAddress: () => null
});

export const useWeb3 = () => useContext(Web3Context);

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [signer, setSigner] = useState(null);
  const [balance, setBalance] = useState('0');
  const [isConnected, setIsConnected] = useState(false);
  
  // Get owner address from environment or NetworkConfigService
  const getOwnerAddress = () => {
    return process.env.REACT_APP_BATCH_PAYOUT_OWNER || 
           networkService.getContractAddress('owner') ||
           '0x786C4D80FCCA08877ffc0D7538e21194212a6860';
  };
  
  // Check if current account is the owner wallet
  const isOwnerWallet = account && account.toLowerCase() === getOwnerAddress().toLowerCase();
  
  // Load saved network preference or use default based on environment
  const getSavedNetwork = () => {
    console.log('Web3Context - Getting saved network');
    console.log('Environment variables:', {
      REACT_APP_FORCE_MAINNET: process.env.REACT_APP_FORCE_MAINNET,
      REACT_APP_ENV: process.env.REACT_APP_ENV,
      NODE_ENV: process.env.NODE_ENV
    });
    
    // Check if mainnet is forced via environment
    const isMainnetForced = process.env.REACT_APP_FORCE_MAINNET === 'true' || 
                           process.env.REACT_APP_ENV === 'production' || 
                           process.env.NODE_ENV === 'production';
    
    console.log('Is mainnet forced?', isMainnetForced);
    console.log('Available networks:', Object.keys(SUPPORTED_NETWORKS));
    console.log('SUPPORTED_NETWORKS.ethereum:', SUPPORTED_NETWORKS.ethereum);
    
    if (isMainnetForced && SUPPORTED_NETWORKS.ethereum) {
      console.log('Forcing Ethereum mainnet:', SUPPORTED_NETWORKS.ethereum.name);
      return SUPPORTED_NETWORKS.ethereum;
    }
    
    const savedNetworkId = localStorage.getItem('selectedNetworkId');
    console.log('Saved network ID from localStorage:', savedNetworkId);
    
    if (savedNetworkId && SUPPORTED_NETWORKS[savedNetworkId]) {
      console.log('Using saved network:', savedNetworkId);
      return SUPPORTED_NETWORKS[savedNetworkId];
    }
    
    console.log('Using default network:', DEFAULT_NETWORK.id);
    return DEFAULT_NETWORK;
  };

  const [selectedNetwork, setSelectedNetworkState] = useState(getSavedNetwork());
  const [selectedToken, setSelectedToken] = useState(() => {
    const savedNetwork = getSavedNetwork();
    return getDefaultToken(savedNetwork.id) || DEFAULT_TOKEN;
  });
  const [networkError, setNetworkError] = useState(null);

  // Refs so MetaMask event handlers always see the latest values without being recreated
  const selectedNetworkRef = useRef(selectedNetwork);
  const selectedTokenRef = useRef(selectedToken);
  const signerRef = useRef(signer);
  useEffect(() => { selectedNetworkRef.current = selectedNetwork; }, [selectedNetwork]);
  useEffect(() => { selectedTokenRef.current = selectedToken; }, [selectedToken]);
  useEffect(() => { signerRef.current = signer; }, [signer]);

  // Wrapper to persist network selection
  const setSelectedNetwork = (network) => {
    setSelectedNetworkState(network);
    localStorage.setItem('selectedNetworkId', network.id);
  };

  // Initialize Web3 on component mount
  useEffect(() => {
    const init = async () => {
      // DISABLED AUTO-CONNECTION: Users must explicitly connect their wallet
      // This allows proper wallet switching between regular and owner wallets
      if (window.ethereum) {
        // Only check for existing connection, don't auto-connect
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          // Log existing connection but don't auto-connect
          if (accounts.length > 0) {
            console.log('Wallet was previously connected:', accounts[0], 'but auto-connection is disabled');
            // Don't auto-connect - user must click connect button
            // This ensures they can choose which wallet to use
          }
        } catch (error) {
          console.error('Error checking connection:', error);
        }
      }
    };

    init();
  }, []);

  const addToHistory = async (payment) => {
    try {
      await apiClient.post(endpoints.payments.history, payment);
      return true;
    } catch (error) {
      console.error('Error adding payment to history:', error);
      if (error.response?.status === 404) {
        toast.error('Payment history service is not available. Please try again later.');
      } else {
        toast.error('Failed to record payment history. Please try again.');
      }
      return false;
    }
  };

  const checkAndSwitchNetwork = async (provider) => {
    try {
      // Skip network check during batch payment
      if (window.BATCH_PAYMENT_IN_PROGRESS) {
        return true;
      }
      
      const network = await provider.getNetwork();
      
      // If already on the correct network, return success
      if (network.chainId === selectedNetwork.chainId) {
        setNetworkError(null);
        return true;
      }
      
      // Need to switch networks
      try {
        // Check if ethereum object exists
        if (!window.ethereum) {
          toast.error('MetaMask not detected');
          setNetworkError('MetaMask not detected');
          return false;
        }
        
        // Handle network switching
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: selectedNetwork.chainIdHex }],
        });
        
        // Verify the switch was successful
        const updatedNetwork = await provider.getNetwork();
        if (updatedNetwork.chainId === selectedNetwork.chainId) {
          setNetworkError(null);
          return true;
        } else {
          setNetworkError(`Failed to verify network switch to ${selectedNetwork.name}`);
          return false;
        }
      } catch (switchError) {
        // This error code indicates that the chain has not been added to MetaMask
        if (switchError.code === 4902) {
          toast.error(`${selectedNetwork.name} network needs to be added to your MetaMask`);
          setNetworkError(`${selectedNetwork.name} network needs to be added to your MetaMask`);
        } else {
          console.error('Failed to switch network:', switchError);
          toast.error(`Failed to switch to ${selectedNetwork.name} network`);
          setNetworkError(`Failed to switch to ${selectedNetwork.name} network`);
        }
        return false;
      }
    } catch (error) {
      console.error('Network check failed:', error);
      toast.error('Network check failed');
      setNetworkError('Network check failed');
      return false;
    }
  };
  
  // Function to switch networks
  const switchNetwork = async (networkId) => {
    if (!window.ethereum || !isConnected) {
      setSelectedNetwork(SUPPORTED_NETWORKS[networkId]);
      // Also update selected token to default for this network
      setSelectedToken(getDefaultToken(networkId));
      return true;
    }
    
    try {
      const targetNetwork = SUPPORTED_NETWORKS[networkId];
      if (!targetNetwork) {
        toast.error('Invalid network selected');
        return false;
      }
      
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: targetNetwork.chainIdHex }],
      });
      
      setSelectedNetwork(targetNetwork);
      
      // Update selected token to default for this network
      const defaultToken = getDefaultToken(networkId);
      setSelectedToken(defaultToken);
      
      // Update balance for new network/token
      updateBalance();
      
      toast.success(`Switched to ${targetNetwork.name} network`);
      setNetworkError(null);
      return true;
    } catch (error) {
      console.error('Failed to switch network:', error);
      toast.error(`Failed to switch to ${SUPPORTED_NETWORKS[networkId].name} network`);
      setNetworkError(`Failed to switch to ${SUPPORTED_NETWORKS[networkId].name} network`);
      return false;
    }
  };

  const connectWallet = async (forceNewConnection = false) => {
    // Check if ethereum object exists in window
    if (window.ethereum) {
      try {
        // If forcing new connection or user wants to switch wallets
        if (forceNewConnection) {
          // Force MetaMask to show account selection dialog
          await window.ethereum.request({
            method: 'wallet_requestPermissions',
            params: [{ eth_accounts: {} }]
          });
        }
        
        // Create Web3Provider instance
        const metamaskProvider = new ethers.providers.Web3Provider(window.ethereum);
        
        // Request account access (will show selection if permissions were requested)
        const accounts = await window.ethereum.request({ 
          method: 'eth_requestAccounts'
        });
        
        if (!accounts || accounts.length === 0) {
          toast.error('Please connect your wallet');
          return false;
        }

        const account = accounts[0];
        
        // Switch to selected network if needed
        const networkOk = await checkAndSwitchNetwork(metamaskProvider);
        if (!networkOk) {
          toast.error(`Please switch to ${selectedNetwork.name} network`);
          return false;
        }

        // Get signer after successful network switch
        const signer = metamaskProvider.getSigner();
        
        // Update state
        setAccount(account);
        setSigner(signer);
        setIsConnected(true);
        
        // Get current network
        const network = await metamaskProvider.getNetwork();
        const chainId = network.chainId;
        
        // Find matching network in our config
        const matchingNetwork = Object.values(SUPPORTED_NETWORKS).find(
          n => n.chainId === chainId
        );
        
        if (matchingNetwork) {
          // Check if mainnet is forced via environment
          const isMainnetForced = process.env.REACT_APP_FORCE_MAINNET === 'true' || 
                                 process.env.REACT_APP_ENV === 'production' || 
                                 process.env.NODE_ENV === 'production';
          
          if (isMainnetForced && matchingNetwork.id !== 'ethereum') {
            // Force switch to mainnet if environment demands it
            console.log('Environment forces mainnet usage, switching networks...');
            const ethereumNetwork = SUPPORTED_NETWORKS.ethereum;
            setSelectedNetwork(ethereumNetwork);
            const defaultToken = getDefaultToken(ethereumNetwork.id);
            if (defaultToken) {
              setSelectedToken(defaultToken);
            }
          } else {
            // Only update if user hasn't saved a preference or if mainnet is forced
            const savedNetworkId = localStorage.getItem('selectedNetworkId');
            if (!savedNetworkId || isMainnetForced) {
              setSelectedNetwork(matchingNetwork);
              // Set appropriate token for this network
              const defaultToken = getDefaultToken(matchingNetwork.id);
              if (defaultToken) {
                setSelectedToken(defaultToken);
              }
            }
            // Otherwise, keep the user's saved preference
          }
        }
        
        // Update balance
        const tokenContract = new ethers.Contract(
          selectedToken.address || networkService.getTokenAddress(selectedToken.symbol),
          ERC20_ABI.abi,
          signer
        );

        const balance = await tokenContract.balanceOf(account);
        const formattedBalance = ethers.utils.formatUnits(balance, selectedToken.decimals);
        setBalance(formattedBalance);
        
        toast.success('Wallet connected successfully!');
        return true;
      } catch (error) {
        console.error('Error connecting wallet:', error);
        if (error.code === 4001) {
          toast.error('Please accept the connection request in your wallet');
        } else {
          toast.error('Failed to connect wallet. Please try again.');
        }
        return false;
      }
    } else {
      // Check if we're on mobile and should use a deep link
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        // Mobile device - use deep link
        window.open('https://metamask.app.link/dapp/' + window.location.href.replace(/^https?:\/\//i, ''), '_blank');
        toast.error('Please open this site with MetaMask browser or install the MetaMask app');
      } else {
        // Desktop - direct to download page
        window.open('https://metamask.io/download/', '_blank');
        toast.error('Please install MetaMask extension to use this feature');
      }
      return false;
    }
  };

  const sendTokenPayment = async (toAddress, amountUSD) => {
    if (!signer) {
      toast.error('Please connect your wallet first');
      return false;
    }

    try {
      // Get MetaMask provider and network
      const metamaskProvider = new ethers.providers.Web3Provider(window.ethereum);
      
      // Check and switch to selected network if needed
      const networkOk = await checkAndSwitchNetwork(metamaskProvider);
      if (!networkOk) {
        return false;
      }

      // Get signer's address
      const address = await signer.getAddress();

      // Create token contract instance using NetworkConfigService
      const tokenContract = new ethers.Contract(
        selectedToken.address || networkService.getTokenAddress(selectedToken.symbol),
        ERC20_ABI.abi,
        signer
      );

      // Convert USD amount to token amount
      const amount = ethers.utils.parseUnits(
        amountUSD.toString(),
        selectedToken.decimals
      );

      // Check token balance
      const balance = await tokenContract.balanceOf(address);
      if (balance.lt(amount)) {
        const formattedBalance = ethers.utils.formatUnits(balance, selectedToken.decimals);
        toast.error(
          `Insufficient ${selectedToken.symbol} balance\n` +
          `Required: ${amountUSD} ${selectedToken.symbol}\n` +
          `Available: ${parseFloat(formattedBalance).toFixed(2)} ${selectedToken.symbol}`
        );
        return false;
      }

      // Get gas price
      const gasPrice = await metamaskProvider.getGasPrice();
      const bufferedGasPrice = gasPrice.mul(130).div(100); // Add 30% buffer

      // Estimate gas with buffer
      const estimatedGas = await tokenContract.estimateGas.transfer(toAddress, amount);
      const bufferedGas = estimatedGas.mul(120).div(100); // Add 20% buffer

      // Calculate total gas cost
      const gasCost = bufferedGasPrice.mul(bufferedGas);

      // Check MATIC balance
      const maticBalance = await metamaskProvider.getBalance(address);
      if (maticBalance.lt(gasCost)) {
        const formattedGasCost = ethers.utils.formatEther(gasCost);
        toast.error(
          `Insufficient MATIC for gas fees\n` +
          `Required: ~${parseFloat(formattedGasCost).toFixed(6)} MATIC\n` +
          `Available: ${parseFloat(ethers.utils.formatEther(maticBalance)).toFixed(6)} MATIC`
        );
        return false;
      }

      // Show gas estimate
      const formattedGasCost = ethers.utils.formatEther(gasCost);
      toast.loading(
        `Estimated gas fee: ${parseFloat(formattedGasCost).toFixed(6)} MATIC\n` +
        'Waiting for confirmation...',
        { id: 'payment' }
      );

      // Send transaction
      const tx = await tokenContract.transfer(toAddress, amount, {
        gasLimit: bufferedGas,
        gasPrice: bufferedGasPrice
      });
      
      toast.loading('Transaction submitted, waiting for confirmation...', { id: 'payment' });
      
      const receipt = await tx.wait();
      
      // Add to history
      await addToHistory({
        id: Date.now(),
        creatorId: account,
        wallet: toAddress,
        amount: amountUSD,
        token: selectedToken.symbol,
        network: selectedNetwork.name,
        timestamp: new Date().toISOString(),
        status: 'Success',
        txHash: receipt.transactionHash
      });
      
      toast.success(
        `Payment of ${amountUSD} ${selectedToken.symbol} sent successfully!\n` +
        `Transaction hash: ${receipt.transactionHash}`,
        { id: 'payment', duration: 5000 }
      );
      return true;
    } catch (error) {
      console.error('Transaction error:', error);
      
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Transaction failed: ${errorMessage}`, { id: 'payment' });
      
      // Add failed transaction to history
      await addToHistory({
        id: Date.now(),
        creatorId: account,
        wallet: toAddress,
        amount: amountUSD,
        token: selectedToken.symbol,
        network: selectedNetwork.name,
        timestamp: new Date().toISOString(),
        status: 'Failed',
        error: errorMessage
      });
      
      return false;
    }
  };

  const updateBalance = async () => {
    if (!signer || !account) {
      setBalance('0');
      return;
    }

    try {
      const metamaskProvider = new ethers.providers.Web3Provider(window.ethereum);
      
      // Create token contract instance using NetworkConfigService
      const tokenContract = new ethers.Contract(
        selectedToken.address || networkService.getTokenAddress(selectedToken.symbol),
        ERC20_ABI.abi,
        signer
      );

      const balance = await tokenContract.balanceOf(account);
      const formattedBalance = ethers.utils.formatUnits(balance, selectedToken.decimals);
      setBalance(formattedBalance);
    } catch (error) {
      console.error('Error updating balance:', error);
      setBalance('0');
    }
  };

  const disconnectWallet = async () => {
    try {
      // Clear React state
      setAccount(null);
      setSigner(null);
      setBalance('0');
      setIsConnected(false);
      
      // Try to revoke permissions from MetaMask (only works in some versions)
      if (window.ethereum && window.ethereum.request) {
        try {
          // This will disconnect the site from MetaMask
          await window.ethereum.request({
            method: 'wallet_revokePermissions',
            params: [{ eth_accounts: {} }]
          });
          console.log('Successfully revoked MetaMask permissions');
        } catch (revokeError) {
          // wallet_revokePermissions might not be supported in all versions
          console.log('Could not revoke permissions (may not be supported):', revokeError);
          // At minimum, the state is cleared above
        }
      }
      
      toast.success('Wallet disconnected');
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
      // Still clear the state even if revoke fails
      setAccount(null);
      setSigner(null);
      setBalance('0');
      setIsConnected(false);
    }
  };
  
  // Function to switch to owner wallet
  const switchToOwnerWallet = async () => {
    try {
      // First disconnect current wallet
      await disconnectWallet();

      // Small delay to ensure disconnection is processed
      await new Promise(resolve => setTimeout(resolve, 500));

      // Connect with force new connection to show wallet selection
      const success = await connectWallet(true);

      if (success) {
        // Read the connected account directly from MetaMask rather than stale state
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        const connectedAccount = accounts[0] || null;
        const ownerAddress = getOwnerAddress();

        if (connectedAccount && connectedAccount.toLowerCase() === ownerAddress.toLowerCase()) {
          toast.success('Successfully connected owner wallet!');
          return true;
        } else {
          toast.error(`Please select the owner wallet: ${ownerAddress.slice(0, 6)}...${ownerAddress.slice(-4)}`);
          return false;
        }
      }

      return false;
    } catch (error) {
      console.error('Error switching to owner wallet:', error);
      toast.error('Failed to switch to owner wallet');
      return false;
    }
  };

  // Listen for account and network changes
  // Handlers use refs so the effect with empty deps doesn't close over stale state
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = async (accounts) => {
        if (accounts.length === 0) {
          // User disconnected their wallet
          setAccount(null);
          setSigner(null);
          setBalance('0');
          toast.error('Wallet disconnected');
        } else {
          const metamaskProvider = new ethers.providers.Web3Provider(window.ethereum);
          // Use the current selectedNetwork from ref to avoid stale closure
          const currentNetwork = selectedNetworkRef.current;
          const network = await metamaskProvider.getNetwork();
          const networkOk = network.chainId === currentNetwork.chainId;
          if (networkOk) {
            setAccount(accounts[0]);
            setSigner(metamaskProvider.getSigner());
            // updateBalance reads selectedToken from its own ref via closure-safe approach
            try {
              const currentToken = selectedTokenRef.current;
              const currentSigner = metamaskProvider.getSigner();
              const tokenContract = new ethers.Contract(
                currentToken.address || networkService.getTokenAddress(currentToken.symbol),
                ERC20_ABI.abi,
                currentSigner
              );
              const bal = await tokenContract.balanceOf(accounts[0]);
              setBalance(ethers.utils.formatUnits(bal, currentToken.decimals));
            } catch (e) {
              console.error('Error updating balance after account change:', e);
              setBalance('0');
            }
            toast.success('Account changed successfully');
          }
        }
      };

      const handleChainChanged = () => {
        // Reload the page as recommended by MetaMask
        window.location.reload();
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      // Check initial connection
      window.ethereum.request({ method: 'eth_accounts' })
        .then(handleAccountsChanged)
        .catch(console.error);

      // Cleanup listeners
      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update balance whenever account changes
  useEffect(() => {
    if (account && signer) {
      updateBalance();
    }
  }, [account, signer]);

  return (
    <Web3Context.Provider
      value={{
        account,
        signer,
        balance,
        isConnected,
        connectWallet,
        sendTokenPayment,
        updateBalance,
        disconnectWallet,
        selectedNetwork,
        setSelectedNetwork,
        selectedToken,
        setSelectedToken,
        switchNetwork,
        networkError,
        isOwnerWallet,
        switchToOwnerWallet,
        getOwnerAddress
      }}
    >
      {children}
    </Web3Context.Provider>
  );
};
